import { filter, get, map, toNumber } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AvatarWithName } from "@/shared/ui/Avatar";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { Column, Row } from "@/shared/ui/Blocks";
import { Icon, IconName } from "@/shared/ui/Icon";
import { IconButton } from "@/shared/ui/IconButton";
import { ModalFileName } from "@/shared/modals";
import { SubtitleText } from "@/shared/ui/Typography";
import { ScreenContainer } from "@/shared/ui/ScreenContainer";
import { SectionedList } from "@/shared/ui/SectionedList";
import Kanban, {
  ChangedKanbanItem,
  KanbanColumnsState,
} from "@/shared/widgets/Kanban";
import { useModal } from "@/shared/utils/hooks/useModal";
import { DropdownMenu } from "@/shared/widgets/DropdownMenu";
import ProjectKanbanCard from "@/features/projects/components/PositionKanbanCard";
import { useReorderKanbanPosition } from "@/features/projects/hooks/useUpdatePositionKanban";
import { useProject } from "@/features/projects/hooks/usePosition";
import { usePeople } from "@/features/people/hooks/usePeople";
import { HeaderContent } from "@/shared/widgets/HeaderContent";
import { Breadcrumbs } from "@/shared/widgets/Breadcrumbs";
import { TabNavigation } from "@/shared/widgets/TabNavigation";
import { useAssessmentsList } from "@/features/assessments/hooks/useAssessmentsList";
import { ListTypeSwitch } from "@/shared/ui/ListTypeSwitch";
import useOrgNavigate from "@/shared/utils/router/useOrgNavigate";
import useDeleteProject from "@/features/projects/hooks/useDeleteProject";
import { EVENT_TYPES, useEvent } from "@/shared/contexts/event.context";
import { RoleType, AccessLevel } from "@/shared/constants";
import { useListType } from "@/shared/ui/ListTypeSwitch/useListType";
import { ResourceEnum } from "@/shared/constants/resources";
import { useCandidateItemActions } from "@/features/projects/hooks/useCandidateItemActions";
import { AssessmentSurveyType } from "@/shared/constants/assessmentSurveys";

export const getProjectNavigationTabs = (
  projectId: number,
  activeId: string
) => [
  {
    id: "overview",
    label: "Assessments",
    isActive: activeId === "overview",
    icon: "Kanban" as IconName,
    path: `/projects/${projectId}`,
  },
  {
    id: "compare",
    icon: "Comparison" as IconName,
    label: "Compare",
    isActive: activeId === "compare",
    path: `/projects/${projectId}/compare`,
  },
  {
    id: "assessmentScorecard",
    icon: "Score" as IconName,
    label: "Assessment Scorecard",
    isActive: activeId === "assessmentScorecard",
    path: `/projects/${projectId}/scorecard`,
  },
  {
    id: "settings",
    icon: "Gear" as IconName,
    label: "Project overview",
    isActive: activeId === "settings",
    path: `/projects/${projectId}/settings`,
  },
];

function ProjectAssessments() {
  const { projectId: _projectId, accountId: _accountId } = useParams();
  const { view } = useListType("kanban");

  const { emitEvent } = useEvent();

  useEffect(() => {
    emitEvent({
      type: EVENT_TYPES.PROJECT_DETAILS_VIEW,
      params: {
        project_id: toNumber(projectId),
      },
    });
  }, []);

  const accountId = toNumber(_accountId);
  const projectId = toNumber(_projectId);

  const navigate = useOrgNavigate();

  const { name, kanban_columns, scorecard_id, org_id, config } = useProject(
    toNumber(projectId),
    true
  );

  const surveysConfig = get(config, "dimensions", []) as AssessmentSurveyType[];

  const { handleConfirmDeleteProject, loading } = useDeleteProject();

  const { assessments } = useAssessmentsList({
    scorecardId: scorecard_id,
  });

  const { people, loading: loadingPeople } = usePeople({
    project_id: toNumber(projectId),
  });

  const { t } = useTranslation();

  const [openInviteModal] = useModal(ModalFileName.ADD_PEOPLE_V2, {
    resource: ResourceEnum.project,
    resourceId: toNumber(projectId),
    resourceName: name,
    defaultRole: RoleType.candidate,
    defaultAccessLevel: AccessLevel.none,
    title: t("hiring.add_candidates"),
  });

  const { handleReorderKanbanPosition } = useReorderKanbanPosition();

  const handleShareAccess = useCallback(
    () => navigate(`/projects/${projectId}/settings/#collaborators`),
    [navigate, projectId]
  );

  const navigationTabs = useMemo(() => {
    const tabs = getProjectNavigationTabs(projectId, "overview");
    const [currentTab, ...restTabs] = tabs;

    return [
      {
        ...currentTab,
        icon: view === "kanban" ? "Kanban" : "List",
      },
      ...restTabs,
    ];
  }, [projectId, view]);

  const positionOptions = [
    {
      label: t("common.share_access"),
      onClick: handleShareAccess,
      icon: "Lock" as IconName,
    },
    {
      label: t("position.archive_position"),
      onClick: () =>
        handleConfirmDeleteProject(projectId, {
          HeaderComponent: <Icon icon="Delete" size="xl" color="red" />,
        }),
      icon: "Delete" as IconName,
    },
  ];

  const getColumnTitle = (title?: string) => {
    if (title === "Shortlist") return "Awaiting data";
    if (title === "Interview & Evaluate") return "Ready to evaluate";
    return title || "";
  };

  const positionColumns = useMemo(
    () =>
      map(kanban_columns, (column, index) => {
        const visiblePeople = map(
          column?.people,
          (candidate, candidateOrder) => {
            const candidateData = people?.find((c) => c?.id === candidate);
            return {
              id: candidateData?.id?.toString(),
              data: {
                ...candidateData,
                avatar_url: candidateData?.avatar,
              },
              columnId: column?.id?.toString(),
              order: candidateOrder,
            };
          }
        ).filter((i) => !!i.id);

        return {
          id: column?.id?.toString(),
          title: getColumnTitle(column?.title || ""),
          subtitle: t("position.candidates", { count: visiblePeople?.length }),
          HeaderRightComponent: column?.title === "Compare" && (
            <IconButton
              icon="Arrow"
              size="md"
              onClick={() =>
                navigate(`projects/${projectId}/compare?compareFilter=compare`)
              }
            />
          ),
          items: visiblePeople,
        };
      }),
    [kanban_columns, people, projectId]
  );

  const getColumnIndex = useCallback(
    (columnId: string) =>
      positionColumns.findIndex((column) => column.id === columnId),
    [positionColumns]
  );

  const { getItemActions } = useCandidateItemActions({
    projectId,
    scorecardId: scorecard_id,
    assessments,
  });

  const renderPositionItem = useCallback(
    (item: any) => (
      <ProjectKanbanCard
        projectId={toNumber(projectId)}
        item={item.data}
        actions={getItemActions(item.data)}
        dimensionConfig={surveysConfig}
      />
    ),
    [projectId, getItemActions, surveysConfig]
  );

  const handleDragEnd = useCallback(
    (changedItem: ChangedKanbanItem, columnsState: KanbanColumnsState) => {
      const itemId = toNumber(changedItem.itemId);

      const updatedPosition = {
        column_id: toNumber(changedItem.columnId),
        order: changedItem.order || 0,
        person_id: itemId,
        position_id: toNumber(projectId),
      };

      handleReorderKanbanPosition(updatedPosition);
    },
    [getColumnIndex]
  );

  const sectionData = useMemo(() => {
    const sections = filter(
      positionColumns,
      (column) => column.items.length > 0
    ).map((column) => ({
      id: column.id,
      title: column.title,
      description: column.subtitle,
      data: column.items,
      onRowClick: (item: any) => {
        navigate(`projects/${projectId}/person/${item.id}`);
      },
    }));
    const columns = {
      name: {
        title: "Name",
        accessor: (item: any) => (
          <AvatarWithName
            direction="row"
            name={item.data.name}
            initials={item.data.initials}
            isContact={item.data.is_contact}
            avatar={item.data.avatar_url}
          />
        ),
      },
      email: {
        span: 3,
        title: "Email",
        accessor: (item: any) => <SubtitleText>{item.data.email}</SubtitleText>,
      },
      status: {
        span: 1,
        title: "Status",
        accessor: (item: any) => item.data.status,
      },
      actions: {
        title: "",
        span: 0,
        accessor: (item: any) => {
          return (
            <Row>
              <DropdownMenu
                items={getItemActions(item.data)}
                separatorIndexes={[0]}
                trigger={
                  <IconButton
                    icon="KebabVertical"
                    color="black"
                    variant="ghost"
                  />
                }
                side="bottom"
                align="end"
              />
            </Row>
          );
        },
      },
    };
    return {
      sections,
      columns,
    };
  }, [positionColumns]);

  return (
    <Column
      className="flex-1 h-[calc(100vh-var(--content-above-height))] max-w-full md:max-w-[calc(100vw-64px)] pb-8 transition-all duration-300 ease-in-out"
      key={projectId + accountId.toString()}
    >
      <Breadcrumbs
        items={[
          {
            label: name || "",
          },
        ]}
      />
      <HeaderContent>
        <Row className="space-x-4 justify-between md:justify-normal items-center">
          <IconButton
            icon="AddUser"
            label={t("hiring.add_candidates")}
            variant="default"
            size="md"
            onClick={() => openInviteModal()}
          />
        </Row>
      </HeaderContent>
      <TabNavigation
        isInpage={false}
        tabs={navigationTabs as any}
        className="px-8 sticky top-0 bg-white z-10"
        scrollContainerId="column-0"
        RightComponent={() => (
          <Row className="flex-1 justify-end">
            <ListTypeSwitch
              defaultListType={view}
              options={["kanban", "list"]}
            />
          </Row>
        )}
      />
      {view === "kanban" && (
        <Kanban
          onDataChange={handleDragEnd}
          columns={positionColumns as any}
          renderItem={renderPositionItem}
          loading={!kanban_columns || loadingPeople}
        />
      )}
      {view === "list" && (
        <ScreenContainer fullWidth className="px-8 max-w-screen-xl">
          <SectionedList
            sections={sectionData.sections as any}
            columns={sectionData.columns}
            loading={!kanban_columns || loadingPeople}
          />
        </ScreenContainer>
      )}
    </Column>
  );
}

export default ProjectAssessments;
